import { useState, useEffect, useMemo } from 'react';

import { useQuery } from '@zenbusiness/application-commons-graphql';
import { useNavigate } from 'react-router-dom';

import { COUNTERPARTY_PAGINATION_DEFAULT_LIMIT } from '#client/constants/pagination';
import { COUNTER_PARTY_STATUS } from '#client/constants/payees';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import { useLogError } from '#client/hooks/useLogError';
import useMemoObject from '#client/hooks/useMemoObject';
import useToastContext from '#client/hooks/useToastContext';
import { GET_LIST_COUNTERPARTY } from '#client/queries';

export default function useCounterparty(
  counterpartyConnectedType,
  status = COUNTER_PARTY_STATUS.ACTIVE,
  isSkip = false,
  limit = COUNTERPARTY_PAGINATION_DEFAULT_LIMIT
) {
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { createCommonErrorToast } = useToastContext();
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const navigate = useNavigate();

  const updateData = (prevData, data) => {
    const counterparties = isLoadingMore
      ? [
          ...prevData.bankingCounterparties.bankingCounterparties,
          ...data.bankingCounterparties.bankingCounterparties
        ]
      : data.bankingCounterparties.bankingCounterparties;

    setIsLoadingMore(false);

    return {
      bankingCounterparties: {
        bankingCounterparties: counterparties,
        pageInfo: data.bankingCounterparties.pageInfo
      }
    };
  };

  const {
    error,
    errors,
    loading: loadingAccounts,
    data: originalData,
    refetch
  } = useQuery(GET_LIST_COUNTERPARTY, {
    // operationName: 'Counterparties',
    variables: {
      page: {
        pageSize: limit,
        page: currentPage
      },
      filter: {
        status: COUNTER_PARTY_STATUS.ALL === status ? undefined : status,
        connectedType: counterpartyConnectedType,
        businessEntityUuid
      }
    },
    skip: !businessEntityUuid || isSkip,
    updateData,
    noThrow: true,
    skipCache: true
  });

  useLogError(errors, error);
  const data = useMemoObject(originalData);

  const accounts = useMemo(
    () => data?.bankingCounterparties?.bankingCounterparties ?? [],
    [data?.bankingCounterparties?.bankingCounterparties],
    { comparison: true }
  );

  const hasMore = useMemo(
    () => !!data?.bankingCounterparties?.pageInfo?.hasNextPage && accounts.length > 0,
    [accounts.length, data?.bankingCounterparties?.pageInfo?.hasNextPage],
    { comparison: true }
  );

  const loadMore = () => {
    if (hasMore) {
      setIsLoadingMore(true);
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const resetPagination = () => {
    if (currentPage !== 1) {
      // reset page to 1
      setCurrentPage(1);
    } else {
      refetch();
    } // force refetch
  };

  useEffect(() => {
    if (error) {
      navigate('', { replace: true });
      createCommonErrorToast({ error });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, navigate]);

  return {
    data,
    accounts,
    error,
    loadingAccounts,
    isLoadingMore,
    hasMore,
    loadMore,
    refetch,
    resetPagination
  };
}
